import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { SkeletonCard } from '../../components/common/SkeletonCard';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ROLE_LEVEL } from '../../utils/constants';
import { Building2, Plus, Pause, Play, Trash2, Edit3, Loader2 } from 'lucide-react';

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-lg border p-4 bg-card">
    <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${accent || ''}`}>{value}</div>
  </div>
);

export default function OrganizationsTab() {
  const { get, post, put, del } = useApi();
  const { user, impersonatingRole } = useAuth();
  const effectiveRole = impersonatingRole || user?.role;
  const isSuperAdmin = effectiveRole === 'super_admin';
  const canManage = (ROLE_LEVEL[effectiveRole] || 0) >= ROLE_LEVEL.admin;

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(null);   // org being edited or 'new'
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingSuspend, setPendingSuspend] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await get('/organizations');
      setOrgs(res.organizations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load organizations');
    }
  }, [get]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <SkeletonCard key={`s${i}`} />)}</div>;

  if (!canManage) {
    return <div className="rounded-lg border p-6 text-muted-foreground">Insufficient permissions.</div>;
  }

  const filtered = orgs.filter(o =>
    !search || o.name.toLowerCase().includes(search.toLowerCase()) || (o.slug || '').includes(search.toLowerCase())
  );

  const totals = orgs.reduce((acc, o) => ({
    users: acc.users + (o.user_count || 0),
    modules: acc.modules + (o.module_count || 0),
    active: acc.active + (o.is_active ? 1 : 0),
  }), { users: 0, modules: 0, active: 0 });

  const handleSuspendToggle = async (org) => {
    setBusy(true);
    try {
      const action = org.is_active ? 'suspend' : 'reactivate';
      await post(`/organizations/${org.org_id}/${action}`, {});
      toast.success(`Organization ${action}d`);
      setPendingSuspend(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await del(`/organizations/${pendingDelete.org_id}`);
      toast.success(`Deleted "${pendingDelete.name}"`);
      setPendingDelete(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-organizations">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Organizations" value={orgs.length} />
        <StatCard label="Active" value={totals.active} accent="text-emerald-600" />
        <StatCard label="Total Users" value={totals.users} />
        <StatCard label="Total Modules" value={totals.modules} />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Input
          placeholder="Search organizations…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="org-search"
        />
        <Button onClick={() => setEditing({ name: '', slug: '', subscription_plan: 'enterprise', is_active: true })} data-testid="add-org-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Organization
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium">Name</th>
              <th className="text-left p-3 font-medium">Slug</th>
              <th className="text-left p-3 font-medium hidden md:table-cell">Plan</th>
              <th className="text-left p-3 font-medium">Users</th>
              <th className="text-left p-3 font-medium">Modules</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="text-right p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No organizations found</td></tr>
            )}
            {filtered.map(o => (
              <tr key={o.org_id} className="border-t hover:bg-muted/30" data-testid="org-row">
                <td className="p-3 font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  {o.name}
                </td>
                <td className="p-3 text-muted-foreground font-mono text-xs">{o.slug}</td>
                <td className="p-3 hidden md:table-cell"><Badge variant="outline">{o.subscription_plan}</Badge></td>
                <td className="p-3 text-muted-foreground">{o.user_count}</td>
                <td className="p-3 text-muted-foreground">{o.module_count}</td>
                <td className="p-3">
                  {o.is_active
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                    : <Badge variant="secondary" className="bg-amber-100 text-amber-700">Suspended</Badge>}
                </td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(o)} data-testid={`edit-org-${o.slug}`} title="Edit">
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPendingSuspend(o)}
                      data-testid={`toggle-org-${o.slug}`}
                      title={o.is_active ? 'Suspend' : 'Reactivate'}
                    >
                      {o.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    {isSuperAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setPendingDelete(o)}
                        data-testid={`delete-org-${o.slug}`}
                        title="Delete (super_admin only)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <OrgEditDialog
        open={!!editing}
        org={editing}
        onClose={() => setEditing(null)}
        onSaved={refresh}
        post={post}
        put={put}
      />

      <AlertDialog open={!!pendingSuspend} onOpenChange={(o) => !o && !busy && setPendingSuspend(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingSuspend?.is_active ? 'Suspend' : 'Reactivate'} "{pendingSuspend?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSuspend?.is_active
                ? 'Users in this org will be blocked from logging in. Reversible.'
                : 'Users will be able to log in again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleSuspendToggle(pendingSuspend)} disabled={busy} data-testid="confirm-suspend-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {pendingSuspend?.is_active ? 'Suspend' : 'Reactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && !busy && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{pendingDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This will <strong>permanently delete</strong> the organization and ALL its data — users, modules, slides, certificates, audit logs.</p>
                <p>This cannot be undone. (Type-the-name confirmation will be added in P0a.4)</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-org-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OrgEditDialog({ open, org, onClose, onSaved, post, put }) {
  const isNew = !org?.org_id;
  const [form, setForm] = useState(() => org || {});
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    setForm(org || {});
    setSlugTouched(false);
  }, [org]);

  if (!open) return null;

  const updateName = (val) => {
    setForm(f => ({
      ...f,
      name: val,
      slug: !slugTouched && isNew ? slugify(val) : f.slug,
    }));
  };

  const submit = async () => {
    if (!form.name || !form.slug) {
      toast.error('Name and slug are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await post('/organizations', {
          name: form.name,
          slug: form.slug,
          subscription_plan: form.subscription_plan || 'enterprise',
        });
        toast.success(`Created "${form.name}"`);
      } else {
        await put(`/organizations/${org.org_id}`, {
          name: form.name,
          subscription_plan: form.subscription_plan,
          is_active: form.is_active,
        });
        toast.success('Saved');
      }
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent data-testid="org-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add organization' : `Edit "${org.name}"`}</DialogTitle>
          <DialogDescription>
            {isNew ? 'A new tenant on the Lumyn platform.' : 'Slug cannot be changed once set.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="org-name">Name</Label>
            <Input
              id="org-name"
              value={form.name || ''}
              onChange={e => updateName(e.target.value)}
              placeholder="e.g. Starlink Qatar"
              data-testid="org-name-input"
            />
          </div>
          <div>
            <Label htmlFor="org-slug">Slug</Label>
            <Input
              id="org-slug"
              value={form.slug || ''}
              onChange={e => { setSlugTouched(true); setForm(f => ({ ...f, slug: slugify(e.target.value) })); }}
              placeholder="starlink-qatar"
              disabled={!isNew}
              data-testid="org-slug-input"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">URL-safe identifier. Lowercase letters, numbers, hyphens only.</p>
          </div>
          <div>
            <Label htmlFor="org-plan">Plan</Label>
            <select
              id="org-plan"
              value={form.subscription_plan || 'enterprise'}
              onChange={e => setForm(f => ({ ...f, subscription_plan: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              data-testid="org-plan-select"
            >
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          {!isNew && (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Toggle to suspend or reactivate the org.</p>
              </div>
              <Switch
                checked={!!form.is_active}
                onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))}
                data-testid="org-active-toggle"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="org-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
